/**
 * Subcomponents extracted out of AdminTeamPanel.jsx to keep the orchestrator
 * focused on data flow + actions rather than 400 lines of JSX.
 *
 * Each piece keeps its existing data-testids so end-to-end tests and the
 * testing agent keep working without any signature changes.
 */
import React from "react";
import { Send, Trash2, Copy, Check, KeyRound, Lock, Plus, Crown } from "lucide-react";

/** Coloured pill used by both invites + admins to surface lifecycle state. */
export const StatusBadge = ({ status, dark }) => {
  const map = {
    active:    { bg: "#064E3B",   fg: "#FFF8EE" },
    pending:   { bg: "#C9A45C",   fg: "#071A2D" },
    accepted:  { bg: "#064E3B",   fg: "#FFF8EE" },
    expired:   { bg: "#071A2D33", fg: "#071A2D" },
    revoked:   { bg: "#FF7A4540", fg: "#FF7A45" },
    suspended: { bg: "#FF7A45",   fg: "#FFF8EE" },
  };
  const s = map[status] || { bg: "#071A2D22", fg: dark ? "#FFF8EE" : "#071A2D" };
  return (
    <span
      className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full"
      style={{ background: s.bg, color: s.fg }}
    >
      {status || "—"}
    </span>
  );
};

/** Banner shown for ~1 action after an invitation is created, exposing the
 *  copy-paste link. The Owner can dismiss it once they've shared it. */
export const InviteCreatedBanner = ({ invite, onCopy, copied, onDismiss }) => (
  <div
    data-testid="invite-created-banner"
    className="p-5 rounded-2xl bg-[#064E3B] text-[#FFF8EE] border border-[#C9A45C]/40"
  >
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#C9A45C] font-semibold">
      <Send size={12}/> Invitation ready
    </div>
    <div className="mt-2 font-serif text-lg">
      {invite.name || invite.email}&apos;s admin invitation is live.
    </div>
    <p className="text-xs opacity-80 mt-1">
      Share this link with {invite.email}. They will set their password and gain access immediately. Expires in 7 days.
    </p>
    <div className="mt-3 flex items-center gap-2 flex-wrap">
      <code
        data-testid="invite-link"
        className="flex-1 min-w-[200px] text-[11px] font-mono px-3 py-2 rounded-xl bg-[#FFF8EE]/10 border border-[#FFF8EE]/15 break-all"
      >
        {window.location.origin}{invite.url}
      </code>
      <button
        data-testid="invite-copy"
        onClick={() => onCopy(invite.url)}
        className="text-xs px-3 py-2 rounded-full bg-[#C9A45C] text-[#071A2D] font-semibold flex items-center gap-1"
      >
        {copied ? <Check size={12}/> : <Copy size={12}/>}
        {copied ? "Copied" : "Copy link"}
      </button>
      <button onClick={onDismiss} className="text-xs underline opacity-70">
        Dismiss
      </button>
    </div>
  </div>
);

/** Collapsible form that lets the Owner mint a new admin invitation. The
 *  permissions toggle group is data-driven from `grantable` so adding new
 *  permissions to the backend automatically surfaces them here. */
export const InviteForm = ({
  showForm, onToggleForm,
  form, onSetField, onTogglePerm,
  grantable, busy, err, onCreate,
}) => (
  <div className="rounded-2xl border border-[#071A2D]/10 bg-[#FFF8EE]">
    <div className="flex items-center justify-between p-4">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[#064E3B] font-semibold">Invite a new admin</div>
        <div className="text-sm text-[#071A2D]/70 mt-1">Only the Founder Owner can invite admins.</div>
      </div>
      <button
        data-testid="invite-new-toggle"
        onClick={onToggleForm}
        className="btn-emerald text-xs px-4 py-2"
      >
        {showForm ? "Cancel" : (<><Plus size={14} className="inline mr-1"/>New invite</>)}
      </button>
    </div>
    {showForm && (
      <div className="p-4 border-t border-[#071A2D]/10 space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <LabeledInput testid="invite-name"  label="Name"  value={form.name}  onChange={(v) => onSetField("name", v)}/>
          <LabeledInput testid="invite-email" label="Email" value={form.email} onChange={(v) => onSetField("email", v)}/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-[#064E3B]/70">Granted permissions</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {grantable.map((p) => (
              <PermToggle
                key={p}
                perm={p}
                on={form.permissions.includes(p)}
                onClick={() => onTogglePerm(p)}
                testid={`invite-perm-${p}`}
              />
            ))}
          </div>
          <div className="text-[11px] text-[#071A2D]/55 mt-2">
            Owner-only permissions (manage_admins, financials, stripe, company settings) can never be granted to an admin.
          </div>
        </div>
        {err && <div data-testid="invite-create-error" className="text-sm text-[#FF7A45]">{err}</div>}
        <button
          data-testid="invite-create-submit"
          onClick={onCreate}
          disabled={busy}
          className="btn-coral text-sm px-5 py-2"
        >
          {busy ? "Creating…" : "Generate invitation link"}
        </button>
      </div>
    )}
  </div>
);

const LabeledInput = ({ testid, label, value, onChange }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-[#064E3B]/70">{label}</label>
    <input
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="kbd-input"
    />
  </div>
);

const PermToggle = ({ perm, on, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`text-[11px] px-3 py-1.5 rounded-full border transition ${
      on
        ? "bg-[#064E3B] text-[#FFF8EE] border-[#064E3B]"
        : "bg-white text-[#064E3B] border-[#064E3B]/25 hover:bg-[#064E3B]/5"
    }`}
  >
    {perm.replace(/_/g, " ")}
  </button>
);

/** Pending-invitations list. Empty-state message inline. */
export const PendingInvitesList = ({ invites, onCopy, onRevoke }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-[#064E3B] font-semibold">Pending invitations</div>
    <div className="space-y-2 mt-3">
      {invites.length === 0 && (
        <div data-testid="invites-empty" className="p-8 text-center text-[#071A2D]/60 rounded-2xl bg-[#FFF8EE] border border-dashed border-[#071A2D]/15">
          No invitations yet.
        </div>
      )}
      {invites.map((inv) => (
        <div
          key={inv.invite_id}
          data-testid={`invite-row-${inv.invite_id}`}
          className="p-4 rounded-2xl card-luxury flex items-center gap-3 flex-wrap"
        >
          <div className="flex-1 min-w-[200px]">
            <div className="font-medium text-sm">{inv.name || inv.email}</div>
            <div className="text-xs text-[#071A2D]/60">{inv.email}</div>
            <div className="flex flex-wrap gap-1 mt-1">
              {(inv.permissions || []).map((p) => (
                <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-[#064E3B]/10 text-[#064E3B]">
                  {p.replace(/_/g, " ")}
                </span>
              ))}
              {(inv.permissions || []).length === 0 && (
                <span className="text-[10px] text-[#071A2D]/50">No granular permissions</span>
              )}
            </div>
          </div>
          <StatusBadge status={inv.status}/>
          {inv.status === "pending" && (
            <>
              <button
                data-testid={`invite-row-copy-${inv.invite_id}`}
                onClick={() => onCopy(inv.url)}
                className="text-xs underline text-[#064E3B] flex items-center gap-1"
              >
                <Copy size={12}/> Copy link
              </button>
              <button
                data-testid={`invite-row-revoke-${inv.invite_id}`}
                onClick={() => onRevoke(inv.invite_id)}
                className="text-xs underline text-[#FF7A45] flex items-center gap-1"
              >
                <Trash2 size={12}/> Revoke
              </button>
            </>
          )}
        </div>
      ))}
    </div>
  </div>
);

/** Single admin row inside the team list. Handles both display + inline edit
 *  modes so the parent only owns which row is currently being edited. */
export const AdminRow = ({
  admin, isOwnerRow, isEditing,
  editPerms, grantable,
  onStartEdit, onTogglePermEdit, onSaveEdit, onCancelEdit,
  onForcePw, onRevoke,
}) => {
  const a = admin;
  return (
    <div
      data-testid={`admin-row-${a.user_id}`}
      className={`p-4 rounded-2xl border ${
        isOwnerRow
          ? "bg-[#071A2D] text-[#FFF8EE] border-[#C9A45C]/40"
          : "bg-[#FFF8EE] border-[#071A2D]/10"
      }`}
    >
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="font-medium flex items-center gap-2">
            {isOwnerRow && <Crown size={14} className="text-[#C9A45C]"/>}
            {a.name || a.email}
          </div>
          <div className={`text-xs mt-0.5 ${isOwnerRow ? "text-[#FFF8EE]/65" : "text-[#071A2D]/60"}`}>
            {a.email} · {isOwnerRow ? "Founder Owner" : "Admin"}
          </div>
        </div>
        {a.requires_password_change && (
          <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-[#C9A45C] text-[#071A2D]">
            Password rotation requested
          </span>
        )}
        <StatusBadge status={a.account_status} dark={isOwnerRow}/>
        {!isOwnerRow && !isEditing && (
          <>
            <button data-testid={`admin-edit-${a.user_id}`}     onClick={() => onStartEdit(a)} className="text-xs underline text-[#064E3B]">
              Edit permissions
            </button>
            <button data-testid={`admin-force-pw-${a.user_id}`} onClick={() => onForcePw(a)}  className="text-xs underline text-[#064E3B] flex items-center gap-1">
              <KeyRound size={12}/> Force password change
            </button>
            <button data-testid={`admin-revoke-${a.user_id}`}   onClick={() => onRevoke(a)}   className="text-xs underline text-[#FF7A45]">
              Revoke
            </button>
          </>
        )}
        {isOwnerRow && (
          <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-[#C9A45C] text-[#071A2D] flex items-center gap-1">
            <Lock size={10}/> Protected
          </span>
        )}
      </div>

      {/* Permissions row */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {(a.permissions || []).map((p) => (
          <span
            key={p}
            className={`text-[10px] px-2 py-0.5 rounded-full ${
              isOwnerRow ? "bg-[#FFF8EE]/15 text-[#FFF8EE]" : "bg-[#064E3B]/10 text-[#064E3B]"
            }`}
          >
            {p.replace(/_/g, " ")}
          </span>
        ))}
        {(a.permissions || []).length === 0 && !isOwnerRow && (
          <span className="text-[10px] text-[#071A2D]/50">No granular permissions</span>
        )}
      </div>

      {/* Inline editor */}
      {isEditing && (
        <div className="mt-4 p-3 rounded-xl bg-white border border-[#064E3B]/15">
          <div className="text-[10px] uppercase tracking-widest text-[#064E3B]/70">Edit permissions</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {grantable.map((p) => (
              <PermToggle
                key={p}
                perm={p}
                on={editPerms.includes(p)}
                onClick={() => onTogglePermEdit(p)}
                testid={`admin-edit-perm-${a.user_id}-${p}`}
              />
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <button data-testid={`admin-edit-save-${a.user_id}`} onClick={() => onSaveEdit(a)} className="btn-emerald text-xs px-4 py-1.5">
              Save
            </button>
            <button onClick={onCancelEdit} className="text-xs underline">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
